import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

import { FiChevronDown } from "react-icons/fi";

import { useUser } from "../../context/UserContext";
import logo from "../../assets/img/logoblanco.png";
import AdminDrawerButton from "../admin-drawer-button";

interface UserNavProps {
  nombreUsuario?: string;
  nombreInmobiliaria?: string;
}

export default function UserNav({
  nombreUsuario,
}: UserNavProps) {
  const [activeMenu, setActiveMenu] = useState<{
    [key: string]: { [key: string]: boolean };
  }>({});
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [toggle, setIsToggle] = useState<boolean>(false);
  const [userMenu, setUserMenu] = useState<boolean>(false);
  const { user, logout } = useUser();

  const fullName = user?.nombre || nombreUsuario || "Usuario";
  const displayName = fullName.split(" ")[0];

  let [scroll, setScroll] = useState<boolean>(false);

  const location = useLocation();
  const current = location.pathname;

  const handleMouseEnter = (menu: string, submenu?: string) => {
    setActiveMenu((prev) => ({
      ...prev,
      [menu]: {
        ...prev[menu],
        [submenu || "main"]: true,
      },
    }));
  };

  const handleMouseLeave = (menu: string, submenu?: string) => {
    setActiveMenu((prev) => ({
      ...prev,
      [menu]: {
        ...prev[menu],
        [submenu || "main"]: false,
      },
    }));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const handlerScroll = () => {
      if (window.scrollY > 50) {
        setScroll(true);
      } else {
        setScroll(false);
      }
    };
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("scroll", handlerScroll);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("scroll", handlerScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const isAdmin = () => user?.rol === "ADMIN";
  const isAdminComite = () => user?.rol === "ADMIN_COMITE";
  const isAsociado = () => user?.rol === "ASOCIADO";
  const isAfiliado = () => user?.rol === "AFILIADO";

  // Opciones del desplegable de usuario, compartidas por el menú de desktop y el de móvil.
  // Cierra el desplegable al elegir una opción (setUserMenu(false)).
  const userMenuLinks = (
    <>
      {isAdmin() && (
        <Link to="/admin-dashboard" onClick={() => setUserMenu(false)}><i className="fa-solid fa-gauge"></i> Panel Administrativo</Link>
      )}
      {isAdminComite() && (
        <>
          <Link to="/admin-comite/dashboard" onClick={() => setUserMenu(false)}><i className="fa-solid fa-gauge"></i> Panel de Comité</Link>
          <Link to="/admin-talleres" onClick={() => setUserMenu(false)}><i className="fa-solid fa-calendar-days"></i> Listado de Talleres</Link>
          <Link to="/admin-create-taller" onClick={() => setUserMenu(false)}><i className="fa-solid fa-plus"></i> Crear Evento</Link>
          <Link to="/escanear-asistencia" onClick={() => setUserMenu(false)}><i className="fa-solid fa-qrcode"></i> Escanear Asistencia</Link>
        </>
      )}
      {(isAsociado() || isAfiliado()) && (
        <Link to="/my-profile" onClick={() => setUserMenu(false)}><i className="fa-solid fa-address-card"></i> Mi Perfil</Link>
      )}
      {(isAsociado() || isAfiliado()) && (
        <Link to="/my-property" onClick={() => setUserMenu(false)}><i className="fa-solid fa-building-circle-check"></i> Mis Propiedades</Link>
      )}
      <Link to="/change-password" onClick={() => setUserMenu(false)}><i className="fa-solid fa-unlock"></i> Cambiar Contraseña</Link>
      {(isAsociado() || isAfiliado()) && (
        <Link to="/mis-cursos" onClick={() => setUserMenu(false)}><i className="fa-solid fa-graduation-cap"></i> Mis Cursos</Link>
      )}
      <button
        onClick={logout}
        className="dropdown-item logout-btn-red"
        style={{ background: "none", border: "none", width: "100%", textAlign: "left", padding: "0.5rem 1rem" }}
      >
        <i className="fa-solid fa-right-from-bracket"></i> <span>Cerrar Sesión</span>
      </button>
    </>
  );

  return (
    <>
      {/* ========== CONVENTIONAL NAVBAR ========== */}
      <div
        className={`header header-light head-shadow ${
          scroll ? "header-fixed" : ""
        }`}
        style={{ backgroundColor: '#0A2F27' }}
      >
        <div className="container">
          <nav
            id="navigation"
            className={
              windowWidth > 991
                ? "navigation navigation-landscape"
                : "navigation navigation-portrait"
            }
          >
            <div className="nav-header" style={{ lineHeight: "0" }}>
              <Link className="nav-brand text-logo" to="/">
                <img src={logo} alt="Logo AMPI Riviera Nayarit" />
              </Link>

              <button
                className="nav-toggle"
                onClick={() => setIsToggle(!toggle)}
                aria-label="Abrir menú de navegación"
                aria-expanded={toggle}
              ></button>

              <div className="mobile_nav">
                <ul>
                  <li>
                    <div className="btn-group account-drop">
                      <button
                        type="button"
                        className="btn p-0 border-0 bg-transparent"
                        onClick={() => setUserMenu(!userMenu)}
                        aria-label="Menú de usuario"
                        aria-expanded={userMenu}
                        style={{ fontSize: "1.2rem", lineHeight: 0, boxShadow: "none" }}
                      >
                        <i className="fa-solid fa-circle-user text-white"></i>
                      </button>
                      <div
                        className="dropdown-menu pull-right animated flipInX"
                        style={{ display: userMenu ? "block" : "none", right: 0, zIndex: 20050 }}
                      >
                        {userMenuLinks}
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            <div
              className={`nav-menus-wrapper ${toggle ? "nav-menus-wrapper-open" : ""}`}
              style={{ transitionProperty: toggle ? "none" : "left" }}
            >
              <button
                className="nav-menus-wrapper-close-button"
                onClick={() => setIsToggle(!toggle)}
                aria-label="Cerrar menú de navegación"
              >
                ✕
              </button>
              <ul className="nav-menu" style={{ float: "right" }}>
                <li className={current === "/" ? "active" : ""}>
                  <Link to="/">Inicio</Link>
                </li>
                <li className={["/classical-layout-with-sidebar"].includes(current) ? "active" : ""}>
                  <Link to="/classical-layout-with-sidebar">Propiedades</Link>
                </li>
                <li
                  className={`${["/agents", "/agent-page", "/asociados", "/asociado-page"].includes(current) ? "active" : ""} ${activeMenu["members"]?.main ? "show" : ""}`}
                  onMouseEnter={() => handleMouseEnter("members")}
                  onMouseLeave={() => handleMouseLeave("members")}
                >
                  <Link to="#">Miembros AMPI<FiChevronDown className="submenu-indicator" /></Link>
                  <ul className="nav-dropdown nav-submenu" style={{ display: activeMenu["members"]?.main ? "block" : "none", pointerEvents: activeMenu["members"]?.main ? "auto" : "none" }}>
                    <li><Link to="/agents" className={current === "/agents" ? "active" : ""}>Afiliados</Link></li>
                    <li><Link to="/asociados" className={current === "/asociados" ? "active" : ""}>Asociados</Link></li>
                  </ul>
                </li>
                <li className={["/agencies", "/agency-page"].includes(current) ? "active" : ""}>
                  <Link to="/agencies">Agencias</Link>
                </li>
                {isAdmin() && (
                  <li
                    className={`${["/escanear-asistencia", "/create-taller", "/admin-dashboard"].includes(current) ? "active" : ""} ${activeMenu["admin"]?.main ? "show" : ""}`}
                    onMouseEnter={() => handleMouseEnter("admin")}
                    onMouseLeave={() => handleMouseLeave("admin")}
                  >
                    <Link to="#">Administrador<FiChevronDown className="submenu-indicator" /></Link>
                    <ul className="nav-dropdown nav-submenu" style={{ display: activeMenu["admin"]?.main ? "block" : "none", pointerEvents: activeMenu["admin"]?.main ? "auto" : "none" }}>
                      <li><Link to="/admin-property" className={current === "/admin-property" ? "active" : ""}>Propiedades MLS</Link></li>
                      <li><Link to="/admin-inm" className={current === "/admin-inm" ? "active" : ""}>Agencias</Link></li>
                      <li><Link to="/admin-afiliado" className={current === "/admin-afiliado" ? "active" : ""}>Afiliados</Link></li>
                      <li><Link to="/admin-asociado" className={current === "/admin-asociado" ? "active" : ""}>Asociados</Link></li>
                      <li><Link to="/admin-talleres" className={current === "/admin-talleres" ? "active" : ""}>Cursos</Link></li>
                      <li><Link to="/aprobar-resena" className={current === "/aprobar-resena" ? "active" : ""}>Aprobar Resenas</Link></li>
                    </ul>
                  </li>
                )}

                {/* User dropdown - al final del menú */}
                <li className="d-none d-lg-inline-block" style={{ marginLeft: 20, borderLeft: "1px solid rgba(255,255,255,0.2)", paddingLeft: 15 }}>
                  <div className="btn-group account-drop">
                    <button
                      type="button"
                      className="btn btn-order-by-filt dropdown-toggle"
                      onClick={() => setUserMenu(!userMenu)}
                    >
                      <i className="fa-solid fa-user avater-img"></i>
                      <span className="ms-2">Hola, {displayName}</span>
                    </button>
                    <div
                      className="dropdown-menu pull-right animated flipInX"
                      style={{ display: userMenu ? "block" : "none" }}
                    >
                      {userMenuLinks}
                    </div>
                  </div>
                </li>
              </ul>

            </div>
            <div
              className="nav-overlay-panel"
              style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", display: toggle ? "block" : "none" }}
            ></div>
          </nav>
        </div>
      </div>
      <div className="clearfix"></div>

      {/* ========== FLOATING BUTTON - SITIO OFICIAL ========== */}
      <a
        href="https://ampirivieranayarit.com/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Visitar sitio oficial AMPI"
        style={{
          position: "fixed", bottom: 70, right: 24, zIndex: 1050,
          width: 52, height: 52, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          backgroundColor: "#1a5d4a",
          boxShadow: "0 4px 16px rgba(26,93,74,0.4)",
          color: "#fff", fontSize: 22, textDecoration: "none",
          transition: "transform 0.2s, box-shadow 0.2s",
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.1)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
      >
        <i className="fa fa-globe" aria-hidden="true"></i>
      </a>

      {/* Acceso global al panel admin — visible solo para ADMIN / admin_comite */}
      <AdminDrawerButton />
    </>
  );
}
